import type { ReactElement } from 'react';
import type { Advertiser, DocumentOptions } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { formatMonthYear } from '../../lib/format';
import { COVER_DEFAULT_IMAGE, resolveHeroImage } from './assets';
import { knobClasses, knobVars } from './knobs';
import { Frame, focalPointStyle } from './primitives';

/**
 * The exact seed string `src/state/document.ts`'s `coverContent()` writes
 * into `content.headline` at assembly time - the only way to tell "still the
 * unedited default" from "a rep already wrote their own title" (both are
 * non-empty strings; there's no separate flag).
 */
const DEFAULT_COVER_HEADLINE = '{{advertiser.name}} Media Proposal';

/**
 * Above this length the client name alone is likely to wrap past one line at
 * the display type size (brief §2: 96px in a ~55%-wide column) - past that
 * point the title steps down to h1 (64px) instead (polish pass).
 */
const LONG_CLIENT_NAME_CHARS = 16;

/**
 * cover (docs/momentum-v3-slides.md "cover"): SPLIT dark. A brand row (client
 * logo + agency logo, large, name-as-text fallback) replaces the old doc-type
 * / client-name pills. The client logo prefers `advertiser.fullLogoUrl` (the
 * brand's full logo/wordmark, plated white on this dark card since most are
 * dark-on-transparent) over the square `advertiser.logoUrl` icon. It sits
 * above a title, a short description, a date + client-url meta
 * row, and a 45%-width hero media slot running to the card edge. `clientName`
 * is derived (never authored twice); date follows the same
 * `presentationDate ?? updatedAt` rule as the other covers, rendered at
 * month/year precision so the proposal does not feel tied to a single day.
 *
 * Zero truncation (contract global rule 2): neither the title nor the
 * description clamp lines any more - the DEFAULT title's explicit two-line
 * split (below) still guarantees "Media Proposal" its own line for a long
 * client name, but a rep-authored title of any length now simply wraps.
 *
 * Client brand panel (Stream D): when `document.options.coverHero` is
 * `'logo'` (the default), the right column becomes a large brand panel
 * instead of the hero photo, and the client mark leaves the small brand row
 * entirely (only the agency mark stays there) - `coverHero: 'photo'` opts
 * back into the photo regardless of what logos are on file. The panel
 * prefers `advertiser.fullLogoUrl` (the wordmark, sized to fill most of the
 * panel); with no wordmark but a square `advertiser.logoUrl` icon on file,
 * the panel falls back to the icon instead, centered and sized smaller
 * (~38% of the panel's shorter side) so it reads as a mark rather than a
 * stretched wordmark - same panel, no extra plate behind it either way (see
 * `coverHeroMode` below and `.mv3-cover-brandpanel-logo-icon` in
 * momentum-v3.css). With neither logo the cover keeps the photo.
 */
export type CoverHeroMode = 'wordmark' | 'icon' | 'photo';

/**
 * Pure derivation of which cover-hero variant renders, split out so it can
 * be unit-tested directly - `web/` tests have no react-dom (see
 * `test/web/momentum-v3-bindings.test.ts`), so the component itself can't
 * be rendered in vitest.
 */
export function coverHeroMode(
  advertiser: Pick<Advertiser, 'fullLogoUrl' | 'logoUrl'>,
  options: Pick<DocumentOptions, 'coverHero'> | null | undefined,
): CoverHeroMode {
  if ((options?.coverHero ?? 'logo') !== 'logo') return 'photo';
  if (advertiser.fullLogoUrl) return 'wordmark';
  if (advertiser.logoUrl) return 'icon';
  return 'photo';
}

export function CoverPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { advertiser } = proposal;
  const theme = proposal.document?.theme ?? null;
  const clientName = advertiser.name ?? section.title;
  // Full logo/wordmark first (wordmark-shaped slot), square icon as fallback.
  const clientFullLogo = advertiser.fullLogoUrl || null;
  const heroMode = coverHeroMode(advertiser, proposal.document?.options);
  // When the brand panel takes over the hero (wordmark or icon), the client
  // logo leaves the small brand row entirely (it is now shown large in the
  // panel) - only the agency mark stays there.
  const usesLogoPanel = heroMode !== 'photo';
  const clientLogo = usesLogoPanel ? null : clientFullLogo || advertiser.logoUrl || null;
  const agencyName = theme?.brandName?.trim() || null;
  const agencyLogo = theme?.logoUrl || null;
  const presented = formatMonthYear(proposal.opportunity?.presentationDate) ?? formatMonthYear(proposal.updatedAt);
  const imageUrl = section.content.imageUrl;
  const heroSrc = resolveHeroImage({ imageUrl, fallback: COVER_DEFAULT_IMAGE });

  const rawHeadline = section.content.headline;
  const isDefaultHeadline = !rawHeadline || rawHeadline === DEFAULT_COVER_HEADLINE;
  const isLongClientName = isDefaultHeadline && (clientName ?? '').trim().length > LONG_CLIENT_NAME_CHARS;
  // Same {{advertiser.name}} token the seed uses (still resolved by the same
  // bindings pipeline) with an explicit newline forced before the fixed
  // suffix - `white-space: pre-line` (momentum-v3-misc.css) renders it as a
  // real line break.
  const headlineValue = isDefaultHeadline ? '{{advertiser.name}}\nMedia Proposal' : rawHeadline;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={['mv3-cover-root', ...knobClasses(section)].join(' ')}
      style={knobVars(section)}
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-cover-grid">
          <div className="mv3-cover-left">
            {agencyLogo || agencyName || (!usesLogoPanel && (clientLogo || clientName)) ? (
              <div className="mv3-cover-brandrow">
                {agencyLogo || agencyName ? (
                  <div className="mv3-cover-brand">
                    {agencyLogo ? (
                      <img src={agencyLogo} alt="" className="mv3-cover-brand-logo" />
                    ) : (
                      <span className="mv3-cover-brand-name">{agencyName}</span>
                    )}
                  </div>
                ) : null}
                {(agencyLogo || agencyName) && !usesLogoPanel && (clientLogo || clientName) ? (
                  <span className="mv3-cover-brand-divider" aria-hidden="true" />
                ) : null}
                {!usesLogoPanel && (clientLogo || clientName) ? (
                  <div className="mv3-cover-brand">
                    {clientLogo ? (
                      <img
                        src={clientLogo}
                        alt=""
                        className={`mv3-cover-brand-logo${clientFullLogo ? ' mv3-cover-brand-logo-full' : ''}`}
                      />
                    ) : (
                      <span className="mv3-cover-brand-name">{clientName}</span>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className={`mv3-cover-title${isLongClientName ? ' mv3-cover-title-compact' : ''}`}>
              <EditableText
                value={headlineValue}
                field="headline"
                placeholder="Add the proposal title"
                readOnly={readOnly}
                variant="headline"
                onSave={(headline) => actions.saveContent(section, { headline })}
              />
            </div>
            <div className="mv3-cover-desc">
              <EditableText
                value={section.content.body}
                field="body"
                placeholder="Add a short description"
                readOnly={readOnly}
                onSave={(body) => actions.saveContent(section, { body })}
              />
            </div>
            <div className="mv3-cover-meta">
              {presented ? <span className="mv3-cover-date">{presented}</span> : null}
              {advertiser.domain ? (
                <>
                  <i className="mv3-meta-divider" aria-hidden="true" />
                  <span className="mv3-cover-site">{advertiser.domain}</span>
                </>
              ) : null}
            </div>
          </div>
          <div className="mv3-cover-hero">
            {usesLogoPanel ? (
              <div className="mv3-cover-brandpanel">
                <img
                  src={(heroMode === 'wordmark' ? clientFullLogo : advertiser.logoUrl) ?? undefined}
                  alt=""
                  className={`mv3-cover-brandpanel-logo${heroMode === 'icon' ? ' mv3-cover-brandpanel-logo-icon' : ''}`}
                />
              </div>
            ) : (
              <>
                <img
                  src={heroSrc}
                  alt={clientName ?? 'Cover image'}
                  className="mv3-cover-image"
                  style={focalPointStyle(section)}
                />
                {!readOnly ? (
                  <ImageSlot
                    url={imageUrl}
                    alt={clientName ?? 'Cover image'}
                    readOnly={readOnly}
                    onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                    className="mv3-cover-image-affordance"
                    hideImage
                    proposalId={proposal.proposalId}
                    defaultQuery={advertiser.industry ?? section.title}
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
